import {
  BannerStripSlice,
  BrandTilesSlice,
  BrandedStoreSliceType,
  BundleStripSlice,
  CampaignHeroSlice,
  CompareTableSlice,
  LearnStripSlice,
  PromoTilesSlice,
} from '@adapters/prismic/types/brandedStorePage';
import mapBannerStripData from '@adapters/prismic/utils/mapBannerStripData';
import mapBrandTilesData from '@adapters/prismic/utils/mapBrandTilesData';
import mapCampaignHeroData from '@adapters/prismic/utils/mapCampaignHeroData';
import mapCompareTableData, {
  CompareTablesById,
  selectCompareTable,
} from '@adapters/prismic/utils/mapCompareTableData';
import mapLearnStripData from '@adapters/prismic/utils/mapLearnStripData';
import mapPromoTilesData from '@adapters/prismic/utils/mapPromoTilesData';
import {
  ActivityNavigationGridSlice,
  transformActivityNavigationGrid,
} from '@adapters/prismic/transforms/activityNavigationGrid';
import {
  RichContentBlockSlice,
  transformRichContentBlock,
} from '@adapters/prismic/transforms/richContentBlock';
import {
  ImageHotspotSlice,
  transformImageHotspot,
} from '@adapters/prismic/transforms/imageHotspot';
import { transformBundleStrip } from '@adapters/prismic/transforms/bundleStrip';
import type { HitProps } from '@components/algolia/Hit';
import {
  ImageProductStripSlice,
  transformImageProductStrip,
} from '@adapters/prismic/transforms/imageProductStrip';
import {
  CategoryNavigationTilesSlice,
  transformCategoryNavigationTiles,
} from '@adapters/prismic/transforms/categoryNavigationTiles';
import {
  FEATURE_FLAG_ACTIVITY_NAVIGATION_GRID,
  FEATURE_FLAG_BANNER_STRIP_SLICE,
  FEATURE_FLAG_BRAND_TILES,
  FEATURE_FLAG_BUNDLE_STRIP,
  FEATURE_FLAG_CATEGORY_NAVIGATION_TILES,
  FEATURE_FLAG_CIP_CLP_COMPARE_TABLE,
  FEATURE_FLAG_IMAGE_HOTSPOT,
  FEATURE_FLAG_IMAGE_PRODUCT_STRIP,
  FEATURE_FLAG_LEARN_STRIP,
  FEATURE_FLAG_MAIN_HERO,
  FEATURE_FLAG_CAMPAIGN_HERO,
  FEATURE_FLAG_PROMO_TILES,
  FEATURE_FLAG_RICH_CONTENT_BLOCK,
} from '@config/feature-flag';

/**
 * Whether the slice handlers render anything for this slice: false when its
 * feature flag is off, or when nothing survives mapping and the component's
 * own empty-content guard would return `null`.
 *
 * The slice zones skip the wrapper for a slice that renders nothing. An empty
 * wrapper would still carry its margin and its `sliceBleedAttributes`, so the
 * adjacency rules would treat it as a band the visible slice above butts
 * against (e.g. promo tiles losing their bottom padding to a flagged-off
 * learn strip).
 *
 * Keep each check in step with its handler case and component guard. Slice
 * types not listed here always render.
 */
export const isSliceRendered = (
  slice?: { slice_type?: string; primary?: unknown; items?: unknown } | null,
  resolved?: {
    compareTables?: CompareTablesById;
    sliceProducts?: Record<string, HitProps>;
  }
): boolean => {
  switch (slice?.slice_type) {
    case BrandedStoreSliceType.mainHero:
      return FEATURE_FLAG_MAIN_HERO;
    case BrandedStoreSliceType.campaignHero:
      // CampaignHero: `if (!h1) return null`
      return (
        FEATURE_FLAG_CAMPAIGN_HERO &&
        Boolean(
          mapCampaignHeroData((slice as CampaignHeroSlice).primary ?? {}).h1
        )
      );
    case BrandedStoreSliceType.promoTiles: {
      if (!FEATURE_FLAG_PROMO_TILES) return false;
      const { primary, items } = slice as PromoTilesSlice;
      // PromoTiles: `if (!tiles.length) return null`
      return mapPromoTilesData(primary, items).tiles.length > 0;
    }
    case BrandedStoreSliceType.learnStrip: {
      if (!FEATURE_FLAG_LEARN_STRIP) return false;
      const { primary, items } = slice as LearnStripSlice;
      const { heading, cards } = mapLearnStripData(primary, items);
      // LearnStrip: `if (!heading || !cards?.length) return null`
      return Boolean(heading) && cards.length > 0;
    }
    case BrandedStoreSliceType.brandTiles: {
      if (!FEATURE_FLAG_BRAND_TILES) return false;
      const { primary, items } = slice as BrandTilesSlice;
      const { heading, tiles } = mapBrandTilesData(primary, items);
      // BrandTiles: `if (!heading || !tiles?.length) return null`
      return Boolean(heading) && tiles.length > 0;
    }
    case BrandedStoreSliceType.compareTable: {
      if (!FEATURE_FLAG_CIP_CLP_COMPARE_TABLE) return false;
      const { primary } = slice as CompareTableSlice;

      return (
        mapCompareTableData(
          primary,
          selectCompareTable(primary, resolved?.compareTables),
          'en'
        ) !== null
      );
    }
    case BrandedStoreSliceType.activityNavigationGrid:
      return (
        FEATURE_FLAG_ACTIVITY_NAVIGATION_GRID &&
        // The locale only shapes hrefs, never whether an item survives.
        transformActivityNavigationGrid(
          slice as unknown as ActivityNavigationGridSlice
        ) !== null
      );
    case BrandedStoreSliceType.richContentBlock:
      return (
        FEATURE_FLAG_RICH_CONTENT_BLOCK &&
        transformRichContentBlock(slice as unknown as RichContentBlockSlice) !==
          null
      );
    case BrandedStoreSliceType.bundleStrip:
      return (
        FEATURE_FLAG_BUNDLE_STRIP &&
        transformBundleStrip(
          slice as BundleStripSlice,
          resolved?.sliceProducts
        ) !== null
      );
    case BrandedStoreSliceType.bannerStrip:
      return (
        FEATURE_FLAG_BANNER_STRIP_SLICE &&
        mapBannerStripData((slice as BannerStripSlice).primary) !== null
      );
    case BrandedStoreSliceType.categoryNavigationTiles:
      return (
        FEATURE_FLAG_CATEGORY_NAVIGATION_TILES &&
        // The locale only shapes hrefs, never whether a tile survives.
        transformCategoryNavigationTiles(
          slice as unknown as CategoryNavigationTilesSlice
        ) !== null
      );
    case 'image_hotspot':
      return (
        FEATURE_FLAG_IMAGE_HOTSPOT &&
        // Resolved products only fill hotspots, never whether a tab survives.
        transformImageHotspot(slice as unknown as ImageHotspotSlice) !== null
      );
    case 'image_product_strip':
      return (
        FEATURE_FLAG_IMAGE_PRODUCT_STRIP &&
        // Resolved products only fill the carousel, never whether it renders.
        transformImageProductStrip(
          slice as unknown as ImageProductStripSlice
        ) !== null
      );
    default:
      return true;
  }
};

/**
 * For each slice, its 0-based position among the rendered slices of its own
 * type on the page, or `null` when it doesn't render (`isSliceRendered`).
 * E.g. the second Rich Content Block is 1 however many other slices sit
 * between them. Analytics `index` values use this (ED-592). One pass, so the
 * zones can use it as their render check too.
 */
export const renderedSliceTypeIndices = (
  slices: ReadonlyArray<Parameters<typeof isSliceRendered>[0]>,
  resolved?: Parameters<typeof isSliceRendered>[1]
): (number | null)[] => {
  const countByType = new Map<string | undefined, number>();

  return slices.map((slice) => {
    if (!isSliceRendered(slice, resolved)) return null;
    const index = countByType.get(slice?.slice_type) ?? 0;
    countByType.set(slice?.slice_type, index + 1);
    return index;
  });
};
